// File: src/pages/SupportFeedback.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LuArrowLeft, LuMessageSquareText } from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import { IoSettingsOutline, IoDocumentTextOutline } from 'react-icons/io5';

import UnsavedChangesBottomSheet from '../components/UnsavedChangesBottomSheet';
import EventHelpTooltip from '../components/EventPreview/EventHelpTooltip';

/* ───────────────────────── Helpers */
const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

/* ───────────────────────── Small UI bits */
function Chip({ icon, label, selected, onClick, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        border: `1px solid ${selected ? '#0b0b0b' : '#e5e7eb'}`,
        background: selected ? '#0b0b0b' : '#fff',
        color: selected ? '#fff' : '#0b0b0b',
        borderRadius: 8,
        padding: '6px 10px', // tighter
        fontSize: 12,
        fontWeight: 800,
        cursor: 'pointer',
      }}
      aria-pressed={selected}
    >
      {icon}
      {label}
    </button>
  );
}

export default function SupportFeedback({
  user,
  feedbackType: propFeedbackType,
  setFeedbackType: propSetFeedbackType,
  feedbackTitle: propFeedbackTitle,
  setFeedbackTitle: propSetFeedbackTitle,
  feedbackMessage: propFeedbackMessage,
  setFeedbackMessage: propSetFeedbackMessage,
  onFeedbackSubmitted,
}) {
  const navigate = useNavigate();

  // Unsaved changes sheet
  const [showUnsavedSheet, setShowUnsavedSheet] = useState(false);

  // Local user (if not passed)
  const [localUser, setLocalUser] = useState(null);

  // Controlled state (fallback to internal if props setters are not provided)
  const [internalFeedbackType, setInternalFeedbackType] = useState('');
  const [internalFeedbackTitle, setInternalFeedbackTitle] = useState('');
  const [internalFeedbackMessage, setInternalFeedbackMessage] = useState('');

  const feedbackType =
    typeof propSetFeedbackType === 'function' ? propFeedbackType : internalFeedbackType;
  const setFeedbackType =
    typeof propSetFeedbackType === 'function' ? propSetFeedbackType : setInternalFeedbackType;

  const feedbackTitle =
    typeof propSetFeedbackTitle === 'function' ? propFeedbackTitle : internalFeedbackTitle;
  const setFeedbackTitle =
    typeof propSetFeedbackTitle === 'function' ? propSetFeedbackTitle : setInternalFeedbackTitle;

  const feedbackMessage =
    typeof propSetFeedbackMessage === 'function' ? propFeedbackMessage : internalFeedbackMessage;
  const setFeedbackMessage =
    typeof propSetFeedbackMessage === 'function' ? propSetFeedbackMessage : setInternalFeedbackMessage;

  // Submit state MUST be before any return
  const [submitting, setSubmitting] = useState(false);

  // Derived user
  const currentUser = user || localUser;

  // Fetch user if needed
  useEffect(() => {
    if (!user) {
      const userId = localStorage.getItem('userId');
      if (userId) {
        axios
          .get(`${process.env.REACT_APP_API_URL}/users/${userId}`)
          .then((res) => setLocalUser(res.data))
          .catch((err) => console.error('Failed to fetch user =>', err));
      }
    }
  }, [user]);

  /* ───────────────────────── Validation */
  const titleMin = 5;
  const titleMax = 25;
  const messageMin = 25;
  const messageMax = 500;
  const validTypes = ['suggestion', 'problem']; // ← updated

  const isTitleTooShort =
    feedbackTitle.trim().length > 0 && feedbackTitle.trim().length < titleMin;
  const isMessageTooShort =
    feedbackMessage.trim().length > 0 && feedbackMessage.trim().length < messageMin;

  const isFormValid =
    validTypes.includes(feedbackType) &&
    feedbackTitle.trim().length >= titleMin &&
    feedbackMessage.trim().length >= messageMin;

  // Dirty check (for unsaved sheet)
  const isDirty =
    (feedbackType?.trim?.() || '') !== '' ||
    (feedbackTitle?.trim?.() || '') !== '' ||
    (feedbackMessage?.trim?.() || '') !== '';

  /* ───────────────────────── Submit */
  const handleSubmit = async () => {
    if (!isFormValid || submitting) return;
    setSubmitting(true);
    try {
      const normalizedType = feedbackType === 'problem' ? 'problem' : 'suggestion';
      const payload = {
        userId: currentUser?._id,
        userName: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim(),
        userEmail: currentUser?.email,
        feedbackType: normalizedType,     // ← send "problem" instead of "issue"
        feedbackTitle,
        feedbackMessage,
        metadata: { source: 'support_feedback_page' },
      };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/feedback`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          },
        }
      );
      
      // Reset
      setFeedbackType('');
      setFeedbackTitle('');
      setFeedbackMessage('');

      // Route back with toast trigger
      navigate('/settings', { state: { feedbackSubmitted: true } });
      onFeedbackSubmitted?.();
    } catch (err) {
      console.error('Feedback submit error =>', err);
      alert(err.response?.data?.message || 'Failed to send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  /* ───────────────────────── Top bar back */
  function handleBack() {
    if (isDirty) {
      document.activeElement?.blur?.();
      setShowUnsavedSheet(true);
    } else {
      navigate('/settings');
    }
  }

  /* ───────────────────────── Render */
  return (
    <div style={S.page}>
      {/* Top bar */}
      <header style={S.header}>
        <div style={S.logoRow}>
          <TribeLogo style={S.logo} />
        </div>

        <div style={S.navRow}>
          <button onClick={handleBack} style={S.backBtn}>
            <LuArrowLeft size={16} color="#fff" />
          </button>

        <div style={S.title}>Support & Feedback</div>

          <div style={S.rightSlot}>
            <EventHelpTooltip
              title="About Support"
              body="Use this form to share a suggestion or report a problem. Include enough detail for our team to understand what happened and follow up if needed."
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main style={S.container}>
        {!currentUser ? (
          <div style={{ padding: 16, color: '#6b7280' }}>Loading user…</div>
        ) : (
          <>
            {/* Centered hero (logo/title/sub) for consistency */}
            <section style={S.hero}>
              <div style={S.iconCircle}>
                <LuMessageSquareText style={{ fontSize: '1.5rem', color: '#000', display: 'block' }} />
              </div>
              <h2 style={S.h2Center}>How can we help?</h2>
              <p style={S.subCenter}>Describe your request or problem</p> {/* ← updated */}
            </section>

            <div style={S.formFrame}>
              {/* Type chips */}
              <section style={{ marginTop: 0 }}>
                <label style={S.label}>Feedback type</label>
                <div style={S.chipsRow} role="radiogroup" aria-label="Feedback type">
                  <Chip
                    icon={<IoSettingsOutline size={16} />}
                    label="Suggestion"
                    selected={feedbackType === 'suggestion'}
                    onClick={() => setFeedbackType('suggestion')}
                    role="radio"
                    aria-checked={feedbackType === 'suggestion'}
                  />
                  <Chip
                    icon={<IoDocumentTextOutline size={16} />}
                    label="Problem"                      
                    selected={feedbackType === 'problem'}  
                    onClick={() => setFeedbackType('problem')} 
                    role="radio"
                    aria-checked={feedbackType === 'problem'} 
                  />
                </div>
                {!validTypes.includes(feedbackType) && (
                  <div style={S.hint}>Select a type to continue</div>
                )}
              </section>

              {/* Title */}
              <section style={{ marginTop: 16 }}>
                <label style={S.label}>Title</label>
                <div style={S.inputWrap}>
                  <input
                    type="text"
                    className="os-input"
                    placeholder="Short summary (e.g., Notifications feel too frequent)"
                    value={feedbackTitle}
                    onChange={(e) => setFeedbackTitle(e.target.value)}
                    maxLength={titleMax}
                    style={{
                      ...S.input,
                      borderColor: isTitleTooShort ? '#C22D2D' : '#e5e7eb',
                    }}
                  />
                  <span style={S.counterInside}>{feedbackTitle.length}/{titleMax}</span>
                </div>
                {isTitleTooShort && (
                  <div style={S.hint}>Min. {titleMin} characters</div>
                )}
              </section>

              {/* Message */}
              <section style={{ marginTop: 16 }}>
                <label style={S.label}>Message</label>
                <div style={S.textareaWrap}>
                  <textarea
                    className="os-input"
                    placeholder="Describe your idea or the problem in more detail…" // ← updated
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    maxLength={messageMax}
                    style={{
                      ...S.textarea,
                      borderColor: isMessageTooShort ? '#C22D2D' : '#e5e7eb',
                    }}
                  />
                  <span style={S.counterInsideTA}>{feedbackMessage.length}/{messageMax}</span>
                </div>
                {isMessageTooShort && (
                  <div style={S.hint}>Min. {messageMin} characters</div>
                )}
              </section>
            </div>

            {/* Sticky bottom bar */}
            <div
              style={{
                ...S.footerBar,
                paddingBottom: `calc(12px + env(safe-area-inset-bottom,0px) + ${isPWA() ? 8 : 0}px)`,
              }}
            >
          
              <button
                onClick={handleBack}
                style={S.secondaryBtn}
                type="button"
              >
                Cancel
              </button>
                  <button
                onClick={handleSubmit}
                disabled={!isFormValid || submitting}
                style={{
                  ...S.primaryBtn,
                  ...(isFormValid && !submitting ? {} : S.primaryBtnDisabled),
                }}
              >
                {submitting ? 'Sending…' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </main>

      {/* Unsaved sheet */}
      {showUnsavedSheet && (
        <UnsavedChangesBottomSheet
          onCancel={() => setShowUnsavedSheet(false)}
          onDiscard={() => {
            setShowUnsavedSheet(false);
            navigate('/settings');
          }}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Styles */
const fontBase =
  "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const S = {
  page: {
    fontFamily: fontBase,
    background: '#fff',
    color: '#0b0b0b',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '0.5rem',
  },
  logo: { width: 72, height: 'auto' },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    height: 44,
    padding: '0.5rem 1rem',
  },
  backBtn: {
    position: 'absolute',
    left: 16,
    background: '#000',
    color: '#fff',
    borderRadius: 999,
    width: 40,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    cursor: 'pointer',
    border: 'none',
  },
  title: {
    fontSize: '0.95rem',
    fontWeight: 900,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  rightSlot: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },

  container: {
    width: '100%',
    maxWidth: 544, // tighter grid width
    margin: '0 auto',
padding: '1rem 1rem calc(180px + env(safe-area-inset-bottom,0px))',
    boxSizing: 'border-box',
  },

  /* Centered hero — restored */
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '4px 0 8px',
  },
  iconCircle: {
    width: 50,
    height: 50,
    borderRadius: 12,
    background: '#f3f4f6',
    display: 'grid',
    placeItems: 'center',
    margin: '0 auto 0px',
  },
  h2Center: {
    fontSize: '1.35rem',
    fontWeight: 850,
    color: '#0b0b0b',
    margin: '12px 0 8px',
    letterSpacing: '-0.035em',
    lineHeight: 1.05,
    textAlign: 'center',
  },
  subCenter: {
    fontSize: '0.92rem',
    color: 'rgba(17,17,17,0.58)',
    margin: '0 0 0rem 0',
    lineHeight: 1.48,
    fontWeight: 500,
    letterSpacing: '-0.01em',
    textAlign: 'center',
  },

  /* Form frame for a clinical look */
  formFrame: {
    marginTop: 12,
    border: '0px solid #eef0f2',
    borderRadius: 12,
    padding: 0,
    marginTop: 30,
    background: '#fff',
  },

  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 6,
    letterSpacing: 0,
    textTransform: 'none',
  },
  chipsRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  hint: {
    marginTop: 6,
    color: '#b91c1c',
    fontSize: 12,
    fontWeight: 600,
  },

  inputWrap: { position: 'relative' },
  textareaWrap: { position: 'relative' },

  input: {
    width: '100%',
    padding: '10px 44px 10px 12px',
    fontSize: 12,
    lineHeight: 1.35,
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    outline: 'none',
    background: '#fff',
    color: '#0b0b0b',
    boxSizing: 'border-box',
    fontFamily: "'Open Sans', sans-serif",
  },
  textarea: {
    width: '100%',
padding: '10px 44px 34px 12px',
    fontSize: 12,
    lineHeight: 1.45,
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    outline: 'none',
    resize: 'vertical',
    minHeight: 220,
    background: '#fff',
    color: '#0b0b0b',
    boxSizing: 'border-box',
    fontFamily: "'Open Sans', sans-serif",
  },

  counterInside: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 11.5,
    color: '#6b7280',
    pointerEvents: 'none',
  },
  counterInsideTA: {
    position: 'absolute',
    right: 10,
    bottom: 8,
    fontSize: 11.5,
    color: '#6b7280',
    pointerEvents: 'none',
  },

  footerBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    background: '#fff',
    borderTop: '1px solid #e5e7eb',
    padding: '10px 12px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    zIndex: 999,
  },
  primaryBtn: {
    width: '100%',
    height: 42,
    background: '#000',
    color: '#fff',
    border: '1px solid #000',
    borderRadius: 8,
    padding: '0 13px',
    fontSize: 12,
    fontWeight: 875,
    letterSpacing: '-0.015em',
    cursor: 'pointer',
  },
  primaryBtnDisabled: {
    background: '#bdbdbd',
    borderColor: '#bdbdbd',
    cursor: 'not-allowed',
  },
  secondaryBtn: {
    width: '100%',
    height: 42,
    background: '#fff',
    color: '#0b0b0b',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '0 13px',
    fontSize: 12,
    fontWeight: 875,
    letterSpacing: '-0.015em',
    cursor: 'pointer',
  },
};